/**
 * DevicePicker — searchable, filterable, content-aware multi-select device list.
 *
 * Built for the Releases deploy flow (single-device targets) but standalone +
 * reusable (Mender deploys, env-group reassignment). Reuses the shared
 * DeviceFilters bar + useTableSearch hook + the .pill-* badges — same pieces as
 * OpsDevices — so it behaves like the rest of the fleet UI at 100+ devices
 * (where the old flat checkbox list was unusable).
 *
 * Content-aware: given each device's current assigned content (`assignedByThing`,
 * supplied by the caller — no extra requests) and the artifact/profile being
 * deployed (`targetProfile`), it shows a current-content column, badges devices
 * already on the target, and can hide them.
 */
import { useMemo, useState } from 'react';
import {
  DeviceFilters,
  type GroupFilter,
  type OnlineFilter,
} from './DeviceFilters';
import { useTableSearch } from './useTableSearch';
import { ENV_GROUP_LABEL, type DeviceSummary, type EnvGroup } from '../api/hooks';

export interface PickerProfile {
  name: string;
  version: string;
}

export function DevicePicker({
  devices,
  groupOf,
  selected,
  onChange,
  assignedByThing,
  targetProfile,
  refresh,
  isFetching,
}: {
  devices: DeviceSummary[];
  groupOf: Map<string, EnvGroup>;
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  /** thing → current assigned content profile (override beats group). */
  assignedByThing?: Map<string, PickerProfile>;
  /** Profile being deployed — drives the "on this profile" badge + Hide toggle. */
  targetProfile?: PickerProfile;
  refresh?: () => void;
  isFetching?: boolean;
}) {
  const [groupFilter, setGroupFilter] = useState<GroupFilter>('all');
  const [onlineFilter, setOnlineFilter] = useState<OnlineFilter>('all');
  const [hideOnTarget, setHideOnTarget] = useState(false);

  const byThing = useMemo(() => new Map(devices.map((d) => [d.thing, d])), [devices]);

  const isOnTarget = (thing: string): boolean => {
    if (!targetProfile) return false;
    const a = assignedByThing?.get(thing);
    return !!a && a.name === targetProfile.name && a.version === targetProfile.version;
  };

  // group + online + already-on-target filters BEFORE search/sort (accurate count).
  const filteredRows = useMemo(() => {
    return devices.filter((d) => {
      if (groupFilter !== 'all' && groupOf.get(d.thing) !== groupFilter) return false;
      if (onlineFilter === 'online' && !d.online) return false;
      if (onlineFilter === 'offline' && d.online) return false;
      if (hideOnTarget && isOnTarget(d.thing)) return false;
      return true;
    });
    // groupOf/assignedByThing are rebuilt by the caller each render — depend on
    // their sources rather than the Map identities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, groupOf, groupFilter, onlineFilter, hideOnTarget, assignedByThing, targetProfile]);

  // Reuse the shared hook for the search box; sort online-first then name below
  // (the hook does single-key sort, we want a stable two-level order).
  const table = useTableSearch(filteredRows, {
    searchAccessors: [
      (d) => d.thing,
      (d) => d.friendly_name,
      (d) => d.serial,
      (d) => d.fw_ver,
    ],
    sortAccessors: { name: (d) => (d.friendly_name ?? d.thing).toLowerCase() },
  });
  const rows = useMemo(
    () =>
      [...table.rows].sort((a, b) => {
        const ao = a.online ? 0 : 1;
        const bo = b.online ? 0 : 1;
        return ao - bo || (a.friendly_name ?? a.thing).localeCompare(b.friendly_name ?? b.thing);
      }),
    [table.rows],
  );

  const toggle = (thing: string) => {
    const next = new Set(selected);
    if (next.has(thing)) next.delete(thing);
    else next.add(thing);
    onChange(next);
  };
  const selectAllMatching = () => {
    const next = new Set(selected);
    for (const d of rows) next.add(d.thing);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <DeviceFilters
        search={table.search}
        setSearch={table.setSearch}
        groupFilter={groupFilter}
        setGroupFilter={setGroupFilter}
        tenantFilter="all"
        setTenantFilter={() => {}}
        onlineFilter={onlineFilter}
        setOnlineFilter={setOnlineFilter}
        tenants={[]}
        showTenant={false}
        searchPlaceholder="Search name, thing, serial, FW…"
        refresh={refresh}
        isFetching={isFetching}
        endSlot={
          targetProfile ? (
            <label className="flex items-center gap-1.5 text-xs text-muted whitespace-nowrap">
              <input
                type="checkbox"
                checked={hideOnTarget}
                onChange={(e) => setHideOnTarget(e.target.checked)}
              />
              Hide devices already on this profile
            </label>
          ) : undefined
        }
      />

      {/* selection toolbar */}
      <div className="flex items-center gap-3 text-xs">
        <span className="text-muted">{selected.size} selected</span>
        <button
          type="button"
          onClick={selectAllMatching}
          disabled={rows.length === 0}
          className="text-accent hover:underline disabled:opacity-40 disabled:no-underline"
        >
          Select all {rows.length} matching
        </button>
        {selected.size > 0 && (
          <button type="button" onClick={() => onChange(new Set())} className="text-muted hover:text-fg">
            Clear
          </button>
        )}
        <span className="ml-auto text-muted">
          {rows.length} of {devices.length} shown
        </span>
      </div>

      {/* selected chips — stay visible even when filtered out of the list */}
      {selected.size > 0 && (
        <div className="flex flex-wrap gap-1 max-h-20 overflow-auto rounded-md bg-soft/50 p-1.5">
          {[...selected].map((t) => {
            const label = byThing.get(t)?.friendly_name || t;
            return (
              <span key={t} className="pill-accent gap-1">
                <span className="truncate max-w-[160px]">{label}</span>
                <button
                  type="button"
                  onClick={() => toggle(t)}
                  aria-label={`Remove ${label}`}
                  className="leading-none hover:text-fg"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* device list */}
      <div className="max-h-72 overflow-auto border border-border rounded-md divide-y divide-border">
        {rows.length === 0 ? (
          <p className="p-3 text-xs text-muted">No devices match.</p>
        ) : (
          rows.map((d) => {
            const onTarget = isOnTarget(d.thing);
            const assigned = assignedByThing?.get(d.thing);
            const group = groupOf.get(d.thing);
            return (
              <label
                key={d.thing}
                className={`flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-soft/60 ${
                  onTarget ? 'opacity-60' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(d.thing)}
                  onChange={() => toggle(d.thing)}
                  className="shrink-0"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">
                    {d.friendly_name || <span className="font-mono text-xs">{d.thing}</span>}
                  </span>
                  {d.friendly_name && (
                    <span className="block font-mono text-[10px] text-muted truncate">{d.thing}</span>
                  )}
                </span>
                {group && <span className="pill-muted shrink-0">{ENV_GROUP_LABEL[group]}</span>}
                <span className={`shrink-0 ${d.online ? 'pill-accent' : 'pill-muted'}`}>
                  {d.online ? 'online' : 'offline'}
                </span>
                <span className="shrink-0 w-28 text-right text-xs truncate">
                  {onTarget ? (
                    <span className="pill-accent">on this profile</span>
                  ) : assigned ? (
                    <span className="text-muted" title={`${assigned.name} v${assigned.version}`}>
                      {assigned.name} <span className="font-mono">v{assigned.version}</span>
                    </span>
                  ) : (
                    <span className="text-muted/70">factory</span>
                  )}
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}
